import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AccordionSection
 *
 * mode='display'     : 기존 읽기 전용 (check_circle 아이콘, itemCheck 기반)
 * mode='interactive' : 입회 모드 - 체크 가능, checks/onCheck props 필요
 * mode='witness'     : 통합 뷰 - 신청자 선택 배지(초록) + 입회자 확인 배지(파랑) 표시
 *                      itemCheck='0'인 항목만 배지 표시, 입회 활성 시 확인 배지 토글 가능
 * locked             : true면 interactive/witness 모드에서도 클릭 비활성화 + 반투명
 */
function AccordionSection({
  title,
  icon,
  isOpen,
  onToggle,
  data,
  labelKey,
  mode = 'display',
  locked = false,
  checks = {},
  onCheck,
}) {
  return (
    <div className="space-y-3">
      <div
        onClick={onToggle}
        className="group flex cursor-pointer items-center justify-between px-1"
      >
        <div className="flex items-center gap-2">
          <span
            className="material-symbols-outlined text-[#5ABD4F]"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            {icon}
          </span>
          <h3 className="font-headline text-lg font-bold text-[#191c1e]">
            {title}
          </h3>
        </div>
        <span
          className={`material-symbols-outlined text-[#becab7] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          expand_more
        </span>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-2 overflow-hidden"
          >
            {data?.length > 0 ? (
              data.map((item, i) => {
                // 입회 모드 (체크 가능)
                if (mode === 'interactive') {
                  const isChecked = checks[i] ?? false;
                  return (
                    <div
                      key={i}
                      onClick={() => !locked && onCheck?.(i, !isChecked)}
                      className={`flex items-center justify-between rounded-2xl border p-4 shadow-sm transition-all ${
                        isChecked
                          ? 'border-[#5ABD4F]/20 bg-white'
                          : 'border-slate-100 bg-slate-50/50'
                      } ${
                        locked
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer active:scale-[0.99]'
                      }`}
                    >
                      <p
                        className={`text-sm font-bold ${
                          isChecked ? 'text-[#191c1e]' : 'text-slate-400'
                        }`}
                      >
                        {item[labelKey]}
                      </p>
                      <span
                        className={`material-symbols-outlined font-bold transition-colors ${
                          isChecked ? 'text-[#5ABD4F]' : 'text-slate-300'
                        }`}
                        style={{
                          fontVariationSettings: isChecked
                            ? "'FILL' 1"
                            : "'FILL' 0",
                        }}
                      >
                        {isChecked ? 'check_circle' : 'radio_button_unchecked'}
                      </span>
                    </div>
                  );
                }

                // 통합 뷰 (신청자 배지 + 입회자 확인 배지)
                if (mode === 'witness') {
                  const isApplied = item.itemCheck === '0';
                  const isWitnessChecked = checks[i] ?? false;
                  return (
                    <div
                      key={i}
                      className={`flex items-center justify-between rounded-2xl border p-4 shadow-sm transition-all ${
                        isApplied
                          ? 'border-[#5ABD4F]/20 bg-white'
                          : 'border-slate-100 bg-slate-50/50 opacity-50'
                      }`}
                    >
                      <p
                        className={`flex-1 pr-3 text-sm font-bold ${
                          isApplied ? 'text-[#191c1e]' : 'text-slate-400'
                        }`}
                      >
                        {item[labelKey]}
                      </p>
                      {isApplied && (
                        <div className="flex shrink-0 items-center gap-1.5">
                          {/* 신청자 선택 배지 (고정) */}
                          <span className="rounded-md bg-[#5ABD4F]/10 px-2 py-0.5 text-[10px] font-extrabold text-[#5ABD4F]">
                            신청 ✓
                          </span>
                          {/* 입회자 확인 배지 (토글 가능) */}
                          <span
                            onClick={() =>
                              !locked && onCheck?.(i, !isWitnessChecked)
                            }
                            className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold transition-all ${
                              isWitnessChecked
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-slate-100 text-slate-400'
                            } ${
                              locked
                                ? 'cursor-not-allowed opacity-60'
                                : 'cursor-pointer active:scale-95'
                            }`}
                          >
                            {isWitnessChecked ? '확인 ✓' : '확인'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }

                const isChecked = item.itemCheck === '0';
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between rounded-2xl border p-4 shadow-sm transition-all ${
                      isChecked
                        ? 'border-[#5ABD4F]/20 bg-white'
                        : 'border-slate-100 bg-slate-50/50 opacity-60'
                    }`}
                  >
                    <p
                      className={`text-sm font-bold ${
                        isChecked ? 'text-[#191c1e]' : 'text-slate-400'
                      }`}
                    >
                      {item[labelKey]}
                    </p>
                    <span
                      className={`material-symbols-outlined font-bold ${
                        isChecked ? 'text-[#5ABD4F]' : 'text-slate-300'
                      }`}
                    >
                      {isChecked ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="rounded-2xl border border-dashed border-[#becab7]/30 bg-white py-4 text-center text-sm text-[#3f4a3b]/40 italic">
                선택된 항목이 없습니다.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default React.memo(AccordionSection);
